import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from "@tanstack/react-table";
import {
  MdEdit,
  MdDelete,
  MdLibraryBooks,
  MdVisibility,
  MdVisibilityOff,
  MdClose,
} from "react-icons/md";
import { FaCrown } from "react-icons/fa";
import {
  TrainingProgram,
  TrainingProgramType,
  DifficultyLevel,
} from "../../types/program";
import type { Challenge } from "../../types/challenge";
import Card from "../card";
import {
  getTrainingProgramTypeLabel,
  getTrainingProgramTypeColor,
  getDifficultyLevelLabel,
  getDifficultyLevelColor,
  normalizeDifficultyLevels,
} from "../../utils/programUtils";
import FilterDropdown, { FilterOption } from "../common/FilterDropdown";
import ActiveFilterBadge from "../common/ActiveFilterBadge";
import { useChallenges } from "../../hooks/useChallenges";
import DataTable from "../common/DataTable";
import SearchForm from "../common/SearchForm";
import LoadingSpinner from "../common/LoadingSpinner";

interface ProgramsTableProps {
  programs: TrainingProgram[];
  loading: boolean;
  onEdit: (program: TrainingProgram) => void;
  onDelete: (program: TrainingProgram) => void;
  onView: (program: TrainingProgram) => void;
  onSearch?: (searchTerm: string) => void;
  onClearSearch?: () => void;
  searchTerm?: string;
  isSearchMode?: boolean;
  challengeId?: number;
  onChallengeChange?: (challengeId: number | undefined) => void;
}

const columnHelper = createColumnHelper<TrainingProgram>();

const ProgramsTable: React.FC<ProgramsTableProps> = ({
  programs,
  loading,
  onEdit,
  onDelete,
  onView,
  onSearch,
  onClearSearch,
  searchTerm = "",
  isSearchMode = false,
  challengeId,
  onChallengeChange,
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const { challenges } = useChallenges();

  React.useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const getProgramChallenges = React.useCallback(
    (program: TrainingProgram): Challenge[] => {
      if (!program.challenges || program.challenges.length === 0) return [];
      return program.challenges;
    },
    []
  );

  // Challenge filter options
  const challengeOptions: FilterOption[] = challenges.map((challenge) => ({
    value: challenge.id.toString(),
    label: `${challenge.name} (${challenge.conducted_at_year})`,
  }));

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && localSearchTerm.trim()) {
      onSearch(localSearchTerm.trim());
    }
  };

  const handleClearSearch = () => {
    setLocalSearchTerm("");
    if (onClearSearch) {
      onClearSearch();
    }
  };

  const columns = useMemo<ColumnDef<TrainingProgram, any>[]>(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: () => "Program Name",
        cell: (info) => {
          const program = info.row.original;
          return (
            <div className="flex min-w-[500px] max-w-[700px] items-center">
              <div className="h-20 w-32 flex-shrink-0">
                {program.training_program_image?.full_path ? (
                  <img
                    src={program.training_program_image.full_path}
                    alt={program.name}
                    className="h-20 w-32 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-20 w-32 items-center justify-center rounded-lg bg-biafit-primary text-white">
                    <MdLibraryBooks className="h-10 w-10" />
                  </div>
                )}
              </div>
              <div className="ml-4">
                <div className="flex items-center space-x-1.5 text-sm font-medium text-navy-700 dark:text-white">
                  {program.is_admin && (
                    <span
                      className="inline-flex h-5 w-5 min-w-5 items-center justify-center rounded-full bg-biafit-primary text-white"
                      aria-label="Admin created program"
                      title="Admin created program"
                    >
                      <FaCrown className="h-3 w-3" />
                    </span>
                  )}
                  <span className="whitespace-break-spaces">
                    {program.name}
                  </span>
                </div>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("program_type", {
        header: () => "Type",
        cell: (info) => {
          const programType = info.getValue() as TrainingProgramType;
          const label = getTrainingProgramTypeLabel(programType);
          const colorClass = getTrainingProgramTypeColor(programType);
          return (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${colorClass}`}
            >
              {label}
            </span>
          );
        },
      }),
      columnHelper.accessor("difficulty_level", {
        header: () => "Difficulty",
        cell: (info) => {
          const program = info.row.original;
          const difficultyLevels = normalizeDifficultyLevels(
            program.difficulty_level as DifficultyLevel | DifficultyLevel[]
          );
          if (difficultyLevels.length === 0) {
            return <span className="text-xs text-gray-500">—</span>;
          }
          return (
            <div className="flex flex-wrap gap-1.5">
              {difficultyLevels.map((level) => (
                <span
                  key={`${program.id}-${level}`}
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getDifficultyLevelColor(
                    level
                  )}`}
                >
                  {getDifficultyLevelLabel(level)}
                </span>
              ))}
            </div>
          );
        },
      }),
      columnHelper.accessor("duration_weeks", {
        header: () => "Duration",
        cell: (info) => {
          const weeks = info.getValue();
          const program = info.row.original;
          return (
            <div className="text-sm text-navy-700 dark:text-white">
              <div>{weeks} weeks</div>
              <div className="text-xs text-gray-500">
                {program.workout_duration}
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("challenges", {
        header: () => "Challenges",
        cell: (info) => {
          const program = info.row.original;
          const programChallenges = getProgramChallenges(program);

          if (programChallenges.length === 0) {
            return <span className="text-xs text-gray-500">—</span>;
          }

          return (
            <div className="flex flex-col gap-1.5">
              {programChallenges.map((challenge) => (
                <div
                  key={challenge.id}
                  className="px-1 text-xs font-medium text-orange-800 dark:text-orange-200"
                >
                  {challenge.name} ({challenge.conducted_at_year})
                </div>
              ))}
            </div>
          );
        },
      }),
      columnHelper.accessor("is_visible", {
        header: () => <div className="text-center">Visible</div>,
        cell: (info) => {
          const isVisible = info.getValue();
          return (
            <div className="flex items-center justify-center">
              {isVisible === false ? (
                <MdVisibilityOff
                  className="h-6 w-6 text-red-600 dark:text-red-500"
                  title="Hidden from users"
                />
              ) : (
                <MdVisibility
                  className="h-6 w-6 text-green-600 dark:text-green-500"
                  title="Visible to users"
                />
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => "Actions",
        cell: (info) => {
          const program = info.row.original;
          return (
            <div className="flex space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(program);
                }}
                className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                title="Edit Program"
              >
                <MdEdit className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(program);
                }}
                className="text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                title="Delete Program"
              >
                <MdDelete className="h-5 w-5" />
              </button>
            </div>
          );
        },
      }),
    ],
    [onEdit, onDelete, getProgramChallenges]
  );

  const table = useReactTable({
    data: programs,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return (
      <Card extra="w-full h-full p-3 md:p-6 sm:overflow-x-auto">
        <LoadingSpinner />
      </Card>
    );
  }

  return (
    <Card extra="w-full h-full p-3 md:p-6 sm:overflow-x-auto">
      <div className="relative flex flex-col space-y-4 pb-8">
        <div className="flex flex-col items-start justify-between space-y-4 sm:flex-row sm:items-center sm:space-y-0">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            Programs Table
          </div>

          {/* Search Bar */}
          <div className="w-full sm:w-auto">
            <SearchForm
              value={localSearchTerm}
              onChange={setLocalSearchTerm}
              onSubmit={handleSearchSubmit}
              onClear={handleClearSearch}
              placeholder="Search programs..."
              hasSearched={isSearchMode}
              loading={loading}
            />
          </div>
        </div>

        {/* Filter Controls */}
        {challenges.length > 0 && (
          <div className="border-t-[1px] border-solid border-gray-200 pt-4 dark:border-gray-700">
            <h3 className="mb-2 text-[15px] font-medium text-gray-700 dark:text-gray-400">
              Filters:
            </h3>

            <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col flex-wrap gap-3 sm:flex-row">
                <div className="w-full sm:w-auto">
                  <FilterDropdown
                    label="Challenge"
                    options={challengeOptions}
                    value={challengeId?.toString()}
                    onChange={(value) =>
                      onChallengeChange?.(
                        value ? parseInt(value, 10) : undefined
                      )
                    }
                    placeholder="All Programs"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Filters */}
        {challengeId && (
          <div className="text-sm text-gray-700 dark:text-gray-400">
            <p className="mb-1 text-[15px] font-medium">Active filters:</p>
            <div className="flex flex-wrap items-center gap-2">
              {isSearchMode && (
                <button
                  onClick={handleClearSearch}
                  className="group flex items-center gap-1.5 rounded bg-blue-100 px-2 py-1 font-medium text-blue-800 transition-colors hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                >
                  <span>Search: "{searchTerm}"</span>
                  <MdClose className="h-3.5 w-3.5 transition-transform group-hover:scale-110" />
                </button>
              )}
              <ActiveFilterBadge
                label={`Challenge: ${
                  challengeOptions.find(
                    (opt) => opt.value === challengeId.toString()
                  )?.label
                }`}
                colorScheme="orange"
                onClear={() => onChallengeChange?.(undefined)}
              />
            </div>
          </div>
        )}
      </div>

      <DataTable
        table={table}
        tableKey={`programs-table-${isSearchMode ? "search" : "regular"}`}
        isEmpty={programs.length === 0}
        empty={{ icon: MdLibraryBooks, title: "No programs found" }}
        onRowClick={(_event, row: TrainingProgram) => onView(row)}
        mobile={programs.map((program) => {
          const difficultyLevels = normalizeDifficultyLevels(
            program.difficulty_level
          );
          return (
            <div
              key={program.id}
              className="cursor-pointer rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-navy-900 md:p-4"
              onClick={() => onView(program)}
            >
              <div className="mb-2 flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="h-16 w-28 flex-shrink-0">
                    {program.training_program_image?.full_path ? (
                      <img
                        src={program.training_program_image.full_path}
                        alt={program.name}
                        className="h-16 w-28 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex h-16 w-28 items-center justify-center rounded-lg bg-biafit-primary">
                        <MdLibraryBooks className="h-8 w-8 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="mb-1 flex flex-col items-start gap-1 text-sm font-medium leading-4 text-navy-700 dark:text-white">
                      {program.is_admin && (
                        <span
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-biafit-primary text-white"
                          aria-label="Admin created program"
                          title="Admin created program"
                        >
                          <FaCrown className="h-3 w-3" />
                        </span>
                      )}
                      <span>{program.name}</span>
                    </h3>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(program);
                    }}
                    className="p-1 text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    title="Edit Program"
                  >
                    <MdEdit className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {program.program_type && (
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getTrainingProgramTypeColor(
                      program.program_type as TrainingProgramType
                    )}`}
                  >
                    {getTrainingProgramTypeLabel(
                      program.program_type as TrainingProgramType
                    )}
                  </span>
                )}
                {difficultyLevels.length > 0 ? (
                  difficultyLevels.map((level) => (
                    <span
                      key={`${program.id}-mobile-${level}`}
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getDifficultyLevelColor(
                        level
                      )}`}
                    >
                      {getDifficultyLevelLabel(level)}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-gray-500">
                    No difficulty set
                  </span>
                )}
                {program.is_visible === false ? (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900 dark:text-red-300">
                    <MdVisibilityOff className="mr-1 h-3.5 w-3.5" />
                    Hidden
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900 dark:text-green-300">
                    <MdVisibility className="mr-1 h-3.5 w-3.5" />
                    Visible
                  </span>
                )}
                {getProgramChallenges(program).map((challenge) => (
                  <div
                    key={`mobile-challenge-${program.id}-${challenge.id}`}
                    className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800 dark:bg-orange-900/50 dark:text-orange-200"
                  >
                    {challenge.name} ({challenge.conducted_at_year})
                  </div>
                ))}
              </div>
              <div className="pt-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-3">
                  {typeof program.duration_weeks !== "undefined" && (
                    <span>{program.duration_weeks} weeks</span>
                  )}
                  {program.workout_duration && (
                    <span className="text-gray-500">
                      {program.workout_duration}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      />
    </Card>
  );
};

export default ProgramsTable;
